import React, { useState } from 'react';
import { Table, Alert, Switch, Badge, Text, CopyButton, Group, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconCurrencyEuro, IconCopy, IconCheck, IconCalendar } from '@tabler/icons-react';

/**
 * Props for the RedactedCode component.
 */
interface RedactedCodeProps {
    /** The sensitive code string to display in redacted form */
    code: string;
}

/**
 * Displays a redacted code that reveals on hover and can be copied to clipboard.
 *
 * Shows bullet characters by default, reveals the actual code on mouse hover,
 * and provides a copy button for easy clipboard access.
 *
 * @param props - The component props
 * @returns A redacted code display with copy functionality
 */
const RedactedCode: React.FC<RedactedCodeProps> = ({ code }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <CopyButton value={code}>
            {({ copied, copy }) => (
                <Group gap={6} wrap="nowrap">
                    <Text
                        component="span"
                        ff="monospace"
                        style={{
                            cursor: 'pointer',
                            userSelect: hovered ? 'text' : 'none',
                            display: 'inline-block',
                            minWidth: `${code.length}ch`,
                        }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={copy}
                    >
                        {hovered ? code : '••••••'}
                    </Text>
                    {copied ? (
                        <IconCheck size={16} color="var(--mantine-color-green-5)" />
                    ) : (
                        <IconCopy size={16} style={{ cursor: 'pointer', opacity: 0.5 }} onClick={copy} />
                    )}
                </Group>
            )}
        </CopyButton>
    );
};

/**
 * Props for the DateDisplay component.
 */
interface DateDisplayProps {
    /** ISO date string to display, or undefined for placeholder */
    dateString?: string;
}

/**
 * Displays a formatted date with smart year handling and full datetime tooltip.
 *
 * Shows month and day by default, reveals year on hover or if the date
 * is from a different year. Includes a calendar icon and displays
 * full datetime in a tooltip.
 *
 * @param props - The component props
 * @returns A formatted date display with tooltip
 */
const DateDisplay: React.FC<DateDisplayProps> = ({ dateString }) => {
    const [hovered, setHovered] = useState(false);
    
    if (!dateString) {
        return <Text>-</Text>;
    }
    
    const date = new Date(dateString);
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const isPastYear = dateYear !== currentYear;
    
    const formatMonthDay = (d: Date): string => {
        return d.toLocaleDateString('fi-FI', { month: 'long', day: 'numeric' });
    };
    
    const formatWithYear = (d: Date): string => {
        return d.toLocaleDateString('fi-FI', { month: 'long', day: 'numeric', year: 'numeric' });
    };
    
    const fullDateTime = date.toLocaleString('fi-FI', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    
    const displayText = hovered || isPastYear 
        ? formatWithYear(date)
        : formatMonthDay(date);
    
    return (
        <Tooltip label={fullDateTime} withArrow position="top">
            <Group gap={6} wrap="nowrap" style={{ cursor: 'default' }}>
                <IconCalendar size={16} style={{ opacity: 0.6 }} />
                <Text
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    {displayText}
                </Text>
            </Group>
        </Tooltip>
    );
};

import { LanBooking } from 'types/intra';
import { isDone } from './intraUtils';

/**
 * Props for the BookingsTable component.
 */
interface BookingsTableProps {
    /** Array of table booking records to display */
    bookings: LanBooking[];
    /** Function to check if an async operation is in progress for a given key */
    isOperating: (key: string) => boolean;
    /** Callback when invitation sent status is toggled */
    onInvitationToggle: (id: string, invitationSent: boolean) => Promise<void>;
    /** Callback when billing/payment status is toggled */
    onBillingToggle: (id: string, done: boolean) => Promise<void>;
    /** Callback when the location badge is clicked to assign a seat */
    onProvisionClick: (booking: LanBooking) => void;
}

/**
 * Displays a table of LAN event table bookings with management controls.
 *
 * Features include:
 * - Redacted booking codes with copy functionality
 * - Email contact information
 * - Location badge display
 * - Toggle switch for invitation sent status
 * - Clickable icon for billing/payment status
 * - Last updated timestamp with smart date display
 *
 * @param props - The component props
 * @returns A table of bookings or an info alert if empty
 */
const BookingsTable: React.FC<BookingsTableProps> = ({
    bookings,
    isOperating,
    onInvitationToggle,
    onBillingToggle,
    onProvisionClick
}) => {
    if (!bookings.length) {
        return <Alert icon={<IconInfoCircle size={16} />} color="blue">Ei aktiivisia pöytävarauksia.</Alert>;
    }

    return (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Koodi</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Sijainti</Table.Th>
                        <Table.Th>Kutsu lähetetty</Table.Th>
                        <Table.Th>Maksettu</Table.Th>
                        <Table.Th>Päivitetty</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {bookings.map(booking => (
                        <Table.Tr key={booking.id}>
                            <Table.Td><RedactedCode code={booking.code} /></Table.Td>
                            <Table.Td>{booking.email}</Table.Td>
                            <Table.Td>
                                <Badge
                                    color="violet.4"
                                    size="xs"
                                    fullWidth
                                    radius="sm"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onProvisionClick(booking)}
                                >
                                    {booking.location || 'Sijoita'}
                                </Badge>
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                <Switch
                                    checked={Boolean(booking.invitationSent)}
                                    onChange={(e) => onInvitationToggle(booking.id, e.currentTarget.checked)}
                                    disabled={isOperating(`booking-invite-${booking.id}`)}
                                />
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }} align="center">
                                <IconCurrencyEuro
                                    size={24}
                                    style={{
                                        cursor: isOperating(`booking-billing-${booking.id}`) ? 'not-allowed' : 'pointer',
                                        opacity: isOperating(`booking-billing-${booking.id}`) ? 0.5 : 1,
                                        color: isDone(booking.done) ? 'var(--mantine-color-violet-5)' : 'var(--mantine-color-gray-5)'
                                    }}
                                    onClick={() => {
                                        if (!isOperating(`booking-billing-${booking.id}`)) {
                                            onBillingToggle(booking.id, !isDone(booking.done));
                                        }
                                    }}
                                />
                            </Table.Td>
                            <Table.Td><DateDisplay dateString={booking.updatedAt ?? booking.createdAt} /></Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </div>
    );
};

export default BookingsTable;

